import React, { useState, useEffect, useCallback } from 'react';
import { X, Building2, Users, UserPlus, Trash2, Plus, Settings, Save, Shield, Mail, ChevronLeft, Search, RefreshCw, Send, Clock, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../utils/api';
import toast from 'react-hot-toast';
import ProcessorSyncTab from './ProcessorSyncTab';

const AuditSettingsPanel = ({ onClose, onClientChanged }) => {
  const { theme } = useTheme();
  const { canAccess, currentUser } = useAuth();
  const canManage = canAccess('audit_tracker_admin') || currentUser?.role === 'director' || currentUser?.admin;
  const isAdminUser = currentUser?.admin === true || currentUser?.role === 'admin';
  const d = theme === 'dark';

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientCanManage, setClientCanManage] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [activeSection, setActiveSection] = useState('team');
  const [globalView, setGlobalView] = useState(null); // null = clients; 'processor_sync' = global mirror sync

  // New client form
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);

  // Add member
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [memberRole, setMemberRole] = useState('auditor');
  const [addingMember, setAddingMember] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await apiGet('/admin/audit_tracker/clients');
      if (res.ok) { const data = await res.json(); setClients(data.clients || []); }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const loadClientDetail = async (clientId) => {
    setLoadingMembers(true);
    try {
      const res = await apiGet(`/admin/audit_tracker/clients/${clientId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedClient(data.client);
        setMembers(data.members || []);
        setClientCanManage(!!data.can_manage_members);
      }
    } catch {} finally { setLoadingMembers(false); }
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) { toast.error('Company name is required'); return; }
    setCreatingClient(true);
    try {
      const res = await apiPost('/admin/audit_tracker/clients', { company_name: newClientName.trim(), contact_email: newClientEmail.trim() || null, contact_phone: newClientPhone.trim() || null });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      const data = await res.json();
      toast.success(data.message);
      if (newClientEmail.trim()?.includes('@') && data.client?.id) {
        if (window.confirm(`Assign "${newClientEmail.trim()}" as client manager?`)) {
          await apiPatch(`/admin/clients/${data.client.id}/assign_manager`, { email: newClientEmail.trim() });
        }
      }
      setNewClientName(''); setNewClientEmail(''); setNewClientPhone(''); setShowNewClient(false);
      loadClients(); onClientChanged?.();
    } catch (err) { toast.error(err.message); } finally { setCreatingClient(false); }
  };

  const loadAvailableUsers = async (clientId) => {
    setAvailableUsers([]);
    setLoadingUsers(true);
    try {
      const res = await apiGet(`/admin/audit_tracker/clients/${clientId}/eligible_users`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || `Failed to load users (${res.status})`);
      } else {
        setAvailableUsers(data.users || []);
        if ((data.users || []).length === 0) {
          toast('No eligible users — all roled users are already members or you lack permission.', { icon: 'ℹ️' });
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load users');
    }
    setLoadingUsers(false);
  };

  const handleAddMember = async () => {
    if (!selectedUserId) { toast.error('Select a user'); return; }
    setAddingMember(true);
    try {
      const res = await apiPost(`/admin/audit_tracker/clients/${selectedClient.id}/members`, { user_public_id: selectedUserId, role: memberRole });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      toast.success((await res.json()).message);
      setSelectedUserId(''); setShowAddMember(false); loadClientDetail(selectedClient.id);
    } catch (err) { toast.error(err.message); } finally { setAddingMember(false); }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.email}?`)) return;
    try {
      await apiDelete(`/admin/audit_tracker/clients/${selectedClient.id}/members/${member.id}`);
      toast.success('Removed'); loadClientDetail(selectedClient.id);
    } catch (err) { toast.error(err.message); }
  };

  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm font-rasa ${
    d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'
  } focus:outline-none focus:ring-1 focus:ring-blue-500/50`;

  const sectionBtn = (key, icon, label) => {
    const Icon = icon;
    const isActive = activeSection === key;
    return (
      <button
        onClick={() => setActiveSection(key)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-rasa font-medium transition-colors w-full text-left ${
          isActive ? (d ? 'bg-gray-700 text-white' : 'bg-blue-50 text-blue-700') : (d ? 'text-gray-400 hover:bg-gray-700/50' : 'text-gray-500 hover:bg-gray-50')
        }`}
      >
        <Icon size={15} /> {label}
      </button>
    );
  };

  return (
    <div className={`${d ? 'text-gray-100' : 'text-gray-800'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {(selectedClient || globalView) && (
            <button onClick={() => { setSelectedClient(null); setActiveSection('team'); setGlobalView(null); }} className={`p-1.5 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <ChevronLeft size={18} />
            </button>
          )}
          <div>
            <h2 className="text-lg font-dm-serif">{globalView === 'processor_sync' ? 'Processor Sync' : selectedClient ? selectedClient.name : 'Audit Settings'}</h2>
            <p className={`text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
              {globalView === 'processor_sync' ? 'Mirror sync monitor + audit file status sync' : selectedClient ? 'Manage team, permissions, and outreach settings' : 'Select a client to configure'}
            </p>
          </div>
        </div>
        <button onClick={onClose} className={`p-2 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <X size={16} />
        </button>
      </div>

      {/* ── Global: Processor Sync (admin/director) ── */}
      {globalView === 'processor_sync' && <ProcessorSyncTab />}

      {/* ── Client List ── */}
      {!selectedClient && !globalView && (
        <div className="space-y-3">
          {/* Global tools (admin/director): mirror sync lives here, not in the tab row */}
          {canManage && (
            <button onClick={() => setGlobalView('processor_sync')} className={`flex items-center justify-between w-full p-4 rounded-xl border transition-all ${d ? 'border-gray-700 hover:border-gray-600 bg-gray-800/30 hover:bg-gray-800/60' : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'}`}>
              <span className="flex items-center gap-2.5">
                <RefreshCw size={16} className="text-blue-500" />
                <span className="text-sm font-dm-serif">Processor Sync</span>
              </span>
              <ChevronLeft size={16} className={`rotate-180 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
            </button>
          )}

          {/* New client button */}
          {canManage && !showNewClient && (
            <button onClick={() => setShowNewClient(true)} className="flex items-center gap-2 px-4 py-2.5 w-full rounded-xl text-[13px] font-rasa font-medium border-2 border-dashed transition-colors ${d ? 'border-gray-600 text-gray-400 hover:border-gray-500' : 'border-gray-300 text-gray-500 hover:border-gray-400'}">
              <Plus size={15} /> Add New Client
            </button>
          )}

          {/* New client form */}
          {showNewClient && (
            <div className={`p-4 rounded-xl border ${d ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <h3 className={`text-sm font-dm-serif mb-3 ${d ? 'text-gray-200' : 'text-gray-700'}`}>New Client</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <input type="text" value={newClientName} onChange={(e) => setNewClientName(e.target.value)} className={inputClass} placeholder="Company name *" />
                <input type="email" value={newClientEmail} onChange={(e) => setNewClientEmail(e.target.value)} className={inputClass} placeholder="Contact email" />
                <input type="tel" value={newClientPhone} onChange={(e) => setNewClientPhone(e.target.value)} className={inputClass} placeholder="Phone" />
              </div>
              <div className="flex gap-2">
                <button onClick={handleCreateClient} disabled={creatingClient} className="px-4 py-2 rounded-lg text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                  {creatingClient ? 'Creating...' : 'Create'}
                </button>
                <button onClick={() => setShowNewClient(false)} className={`px-4 py-2 rounded-lg text-[13px] font-rasa ${d ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>Cancel</button>
              </div>
            </div>
          )}

          {/* Client cards */}
          {loading ? (
            <div className="text-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto" /></div>
          ) : clients.length === 0 ? (
            <div className={`text-center py-12 rounded-xl border ${d ? 'border-gray-700' : 'border-gray-200'}`}>
              <Building2 size={32} className={`mx-auto mb-3 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
              <p className={`text-sm font-dm-serif ${d ? 'text-gray-300' : 'text-gray-600'}`}>No clients yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => loadClientDetail(client.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    d ? 'border-gray-700 hover:border-gray-600 bg-gray-800/30 hover:bg-gray-800/60' : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-dm-serif">{client.name}</div>
                      <div className={`text-[12px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                        {client.member_count} team · {client.audit_count} audit{client.audit_count !== 1 ? 's' : ''}
                        {client.contact_email && ` · ${client.contact_email}`}
                      </div>
                    </div>
                    <ChevronLeft size={16} className={`rotate-180 ${d ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Client Detail (tabbed sections) ── */}
      {selectedClient && (
        <div className="flex gap-4">
          {/* Sidebar nav */}
          <div className="w-40 shrink-0 space-y-1">
            {sectionBtn('team', Users, 'Team')}
            {sectionBtn('permissions', Shield, 'Permissions')}
            {sectionBtn('email', Mail, 'Outreach')}
            {sectionBtn('dashboard', Settings, 'Dashboard')}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* ── Team Section ── */}
            {activeSection === 'team' && (
              <div className={`rounded-xl border p-5 ${d ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-dm-serif">Audit Team</h3>
                  {clientCanManage && (
                    <button onClick={() => { setShowAddMember(true); loadAvailableUsers(selectedClient.id); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700">
                      <UserPlus size={13} /> Add Member
                    </button>
                  )}
                </div>

                {showAddMember && (
                  <div className={`p-3 rounded-lg mb-4 border ${d ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                    {loadingUsers ? (
                      <div className="text-center py-3"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto" /></div>
                    ) : (
                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className={`block text-[11px] font-rasa font-bold mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>User</label>
                          <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className={inputClass}>
                            <option value="">Select user...</option>
                            {availableUsers.map((u) => <option key={u.id} value={u.id}>{u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim() : u.username} — {u.role} ({u.email})</option>)}
                          </select>
                        </div>
                        <div className="w-28">
                          <label className={`block text-[11px] font-rasa font-bold mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Role</label>
                          <select value={memberRole} onChange={(e) => setMemberRole(e.target.value)} className={inputClass}>
                            <option value="lead">Lead</option>
                            <option value="auditor">Auditor</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </div>
                        <button onClick={handleAddMember} disabled={addingMember || !selectedUserId} className="px-3 py-2 bg-blue-600 text-white rounded-lg text-[12px] font-rasa disabled:opacity-40">Add</button>
                        <button onClick={() => setShowAddMember(false)} className={`p-2 rounded-lg ${d ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}><X size={14} /></button>
                      </div>
                    )}
                  </div>
                )}

                {loadingMembers ? (
                  <div className="text-center py-6"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto" /></div>
                ) : members.length === 0 ? (
                  <p className={`text-center py-6 text-sm font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>No team members yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {members.map((m) => (
                      <div key={m.id} className={`flex items-center justify-between p-3 rounded-lg ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                        <div>
                          <div className={`text-sm font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                            {m.first_name ? `${m.first_name} ${m.last_name || ''}`.trim() : m.username}
                          </div>
                          <div className={`text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{m.email}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[11px] font-rasa font-bold px-2 py-0.5 rounded ${
                            m.role === 'lead' ? (d ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700')
                            : m.role === 'auditor' ? (d ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
                            : (d ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600')
                          }`}>{m.role}</span>
                          {clientCanManage && (isAdminUser || !['admin', 'director'].includes(m.user_role)) && (
                            <button onClick={() => handleRemoveMember(m)} className="p-1 text-red-400 hover:text-red-500"><Trash2 size={13} /></button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Permissions Section ── */}
            {activeSection === 'permissions' && (
              <div className={`rounded-xl border p-5 ${d ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
                <h3 className="text-sm font-dm-serif mb-1">Role Permissions</h3>
                <p className={`text-[12px] font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  Configure what each audit role can see and do. Directors and Admins always have full access.
                </p>
                <RolePermissionsEditor client={selectedClient} isDark={d} />
              </div>
            )}

            {/* ── Outreach Section (cadence overview + sender config) ── */}
            {activeSection === 'email' && (
              <div className="space-y-4">
                <OutreachCadenceOverview isDark={d} />
                <div className={`rounded-xl border p-5 ${d ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
                  <h3 className="text-sm font-dm-serif mb-1">Email Sender</h3>
                  <p className={`text-[12px] font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                    Choose who sends audit emails for this client.
                  </p>
                  <EmailSenderConfig client={selectedClient} isDark={d} />
                </div>
              </div>
            )}

            {/* ── Dashboard Config Section ── */}
            {activeSection === 'dashboard' && (
              <div className={`rounded-xl border p-5 ${d ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
                <h3 className="text-sm font-dm-serif mb-1">Client Dashboard</h3>
                <p className={`text-[12px] font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  Control what the client sees in their audit dashboard.
                </p>
                <DashboardConfigEditor client={selectedClient} isDark={d} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Outreach Cadence Overview ────────────────────────────────
// Informational: reflects the contact-deduped CRM cadence (replaces the old
// per-property 6-tier templates). Intervals are system defaults today.
const OutreachCadenceOverview = ({ isDark: d }) => {
  const steps = [
    { icon: Send, label: 'Initial digest', when: 'Day 0', desc: 'One email per contact, per owner — lists every outstanding property with a units CSV.' },
    { icon: Clock, label: 'Reminder 1', when: '+7 days', desc: 'Re-sent only for what is still outstanding (received docs drop off automatically).' },
    { icon: Clock, label: 'Reminder 2', when: '+14 days', desc: 'Final automated nudge in the sequence.' },
    { icon: AlertTriangle, label: 'Unresponsive', when: 'After +14d', desc: 'Auto-sending stops; a task is created for the assigned staff member to follow up.' },
  ];
  return (
    <div className={`rounded-xl border p-5 ${d ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'}`}>
      <h3 className="text-sm font-dm-serif mb-1">Outreach Cadence</h3>
      <p className={`text-[12px] font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
        How contacts are chased for outstanding documents. Digests are deduped per contact and recomputed at send time. Manage live sends in the Contacts tab.
      </p>
      <div className="space-y-2">
        {steps.map((s) => (
          <div key={s.label} className={`flex items-start gap-3 p-3 rounded-lg ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
            <s.icon size={15} className="text-blue-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-[13px] font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>{s.label}</span>
                <span className={`text-[11px] font-rasa px-1.5 py-0.5 rounded ${d ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{s.when}</span>
              </div>
              <div className={`text-[12px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{s.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <p className={`text-[11px] font-rasa mt-3 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
        Contacts upload to the processor portal; the mirror confirms receipt and cancels further reminders. Editable intervals are coming soon.
      </p>
    </div>
  );
};

// ── Role Permissions Editor ──────────────────────────────────
const RolePermissionsEditor = ({ client, isDark: d }) => {
  const defaults = {
    lead:    { see_all: true,  can_write: true,  can_admin: true  },
    auditor: { see_all: false, can_write: true,  can_admin: false },
    viewer:  { see_all: true,  can_write: false, can_admin: false },
  };
  const saved = client?.settings?.audit_role_config || {};
  const [config, setConfig] = useState({
    lead:    { ...defaults.lead,    ...saved.lead },
    auditor: { ...defaults.auditor, ...saved.auditor },
    viewer:  { ...defaults.viewer,  ...saved.viewer },
  });
  const [dirty, setDirty] = useState(false);

  const toggle = (role, key) => { setConfig((p) => ({ ...p, [role]: { ...p[role], [key]: !p[role][key] } })); setDirty(true); };

  const save = async () => {
    try {
      const res = await apiPatch(`/admin/clients/${client.id}`, { settings: JSON.stringify({ ...client.settings, audit_role_config: config }) });
      if (res.ok) { toast.success('Permissions saved'); setDirty(false); } else toast.error('Failed');
    } catch (err) { toast.error(err.message); }
  };

  const roles = [{ key: 'lead', label: 'Lead', desc: 'Team leader — manages auditors' }, { key: 'auditor', label: 'Auditor', desc: 'Reviews properties and files' }, { key: 'viewer', label: 'Viewer', desc: 'Read-only access' }];
  const perms = [{ key: 'see_all', label: 'See All Properties', desc: 'Off = only see assigned' }, { key: 'can_write', label: 'Take Actions', desc: 'Emails, transitions, mark received' }, { key: 'can_admin', label: 'Admin', desc: 'Import, templates, bulk' }];

  return (
    <div className="space-y-3">
      {roles.map((r) => (
        <div key={r.key} className={`p-4 rounded-lg ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
          <div className="flex items-center justify-between mb-2">
            <div>
              <span className={`text-sm font-rasa font-bold ${d ? 'text-gray-200' : 'text-gray-700'}`}>{r.label}</span>
              <span className={`text-[11px] font-rasa ml-2 ${d ? 'text-gray-500' : 'text-gray-400'}`}>{r.desc}</span>
            </div>
          </div>
          <div className="flex items-center gap-5">
            {perms.map((p) => (
              <label key={p.key} className="flex items-center gap-2 cursor-pointer" title={p.desc}>
                <input type="checkbox" checked={config[r.key]?.[p.key] ?? false} onChange={() => toggle(r.key, p.key)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <div>
                  <div className={`text-[12px] font-rasa font-medium ${d ? 'text-gray-300' : 'text-gray-600'}`}>{p.label}</div>
                  <div className={`text-[10px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{p.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}
      {dirty && (
        <button onClick={save} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700">
          <Save size={14} /> Save Permissions
        </button>
      )}
    </div>
  );
};

// ── Email & Follow-up Config ──────────────────────────────────
const EmailSenderConfig = ({ client, isDark: d }) => {
  const [senders, setSenders] = useState([]);
  const [defaultSenderId, setDefaultSenderId] = useState(client?.settings?.default_sender_id || '');
  const [cadence, setCadence] = useState([5, 10, 15, 20, 25, 30]);
  const [autoEmailEnabled, setAutoEmailEnabled] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [editingTier, setEditingTier] = useState(null); // tier number to edit template
  const [templates, setTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({ subject_template: '', body_template: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    apiGet(`/admin/audit_tracker/available_senders?client_id=${client?.id}`)
      .then((r) => r.ok ? r.json() : { senders: [] })
      .then((data) => setSenders(data.senders || []))
      .catch(() => {});

    const settings = client?.settings || {};
    setCadence(settings.follow_up_cadence_days || [5, 10, 15, 20, 25, 30]);
    setAutoEmailEnabled(settings.auto_email_enabled !== false);

    // Load templates for this client
    apiGet(`/admin/audit_tracker/email_templates?client_id=${client?.id}`)
      .then((r) => r.ok ? r.json() : { templates: [] })
      .then((data) => setTemplates(data.templates || []))
      .catch(() => {});
  }, [client]);

  const openTierTemplate = (tierNum) => {
    const existing = templates.find((t) => t.tier === tierNum);
    setTemplateForm({
      subject_template: existing?.subject_template || '',
      body_template: existing?.body_template || '',
    });
    setEditingTier(tierNum);
  };

  const saveTierTemplate = async () => {
    setSavingTemplate(true);
    try {
      const existing = templates.find((t) => t.tier === editingTier);
      let res;
      if (existing) {
        res = await apiPatch(`/admin/audit_tracker/email_templates/${existing.id}`, { ...templateForm, tier: editingTier, active: true });
      } else {
        res = await apiPost('/admin/audit_tracker/email_templates', { ...templateForm, tier: editingTier, client_id: client.id, active: true });
      }
      if (res.ok) {
        toast.success(`Tier ${editingTier} template saved`);
        setEditingTier(null);
        // Reload templates
        const r = await apiGet(`/admin/audit_tracker/email_templates?client_id=${client?.id}`);
        if (r.ok) { const d = await r.json(); setTemplates(d.templates || []); }
      } else { const d = await res.json(); toast.error(d.error || 'Failed'); }
    } catch (err) { toast.error(err.message); }
    setSavingTemplate(false);
  };

  const updateCadence = (tier, value) => {
    const newCadence = [...cadence];
    newCadence[tier] = Math.max(1, parseInt(value) || 5);
    setCadence(newCadence);
    setDirty(true);
  };

  const save = async () => {
    try {
      const newSettings = {
        ...client.settings,
        default_sender_id: defaultSenderId || null,
        follow_up_cadence_days: cadence,
        auto_email_enabled: autoEmailEnabled,
      };
      const res = await apiPatch(`/admin/clients/${client.id}`, { settings: JSON.stringify(newSettings) });
      if (res.ok) { toast.success('Email settings saved'); setDirty(false); } else toast.error('Failed');
    } catch (err) { toast.error(err.message); }
  };

  const inputClass = `px-3 py-1.5 rounded-lg border text-sm font-rasa text-center w-16 ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500/50`;

  const TIER_INFO = [
    { label: 'Tier 1', tone: 'Professional, warm', subject: 'Audit File Request' },
    { label: 'Tier 2', tone: 'Friendly reminder', subject: 'Reminder: Audit Files' },
    { label: 'Tier 3', tone: 'Firm, direct', subject: 'Second Notice: Audit Files' },
    { label: 'Tier 4', tone: 'Urgent', subject: 'URGENT: Audit Files Past Due' },
    { label: 'Tier 5', tone: 'Final warning', subject: 'FINAL NOTICE: Audit Files' },
    { label: 'Tier 6', tone: 'No email — mark unresponsive', subject: '(Internal escalation only)' },
  ];

  return (
    <div className="space-y-5">
      {/* Default Sender */}
      <div>
        <label className={`block text-[12px] font-rasa font-bold mb-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Default Email Sender</label>
        <select
          value={defaultSenderId}
          onChange={(e) => { setDefaultSenderId(e.target.value); setDirty(true); }}
          className={`w-full px-3 py-2 rounded-lg border text-sm font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
        >
          <option value="">JPro System Email (jpro@zeffert.com)</option>
          {senders.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} ({s.email}) {s.outlook_connected ? '— Outlook' : '— SMTP'}
            </option>
          ))}
        </select>
        <p className={`text-[11px] font-rasa mt-1 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
          If Outlook connected, sends from their mailbox via Graph API. Otherwise falls back to SMTP.
        </p>
      </div>

      {/* Auto-email toggle */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={autoEmailEnabled}
            onChange={(e) => { setAutoEmailEnabled(e.target.checked); setDirty(true); }}
            className="rounded border-gray-300 text-blue-600"
          />
          <div>
            <div className={`text-[13px] font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>Enable automated follow-up emails</div>
            <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>When on, the system sends follow-ups on the schedule below. When off, follow-ups are manual only.</div>
          </div>
        </label>
      </div>

      {/* Follow-up Schedule */}
      <div>
        <label className={`block text-[12px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Follow-up Schedule (business days between tiers)</label>
        <p className={`text-[11px] font-rasa mb-3 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
          After a file request or setup request is sent, the system sends escalating follow-ups if the property hasn't responded. Each tier increases in urgency.
        </p>

        <div className="space-y-2">
          {TIER_INFO.map((tier, i) => {
            const tierNum = i + 1;
            const hasTemplate = templates.some((t) => t.tier === tierNum);
            return (
              <div key={i}>
                <div className={`flex items-center gap-3 p-3 rounded-lg ${editingTier === tierNum ? (d ? 'bg-gray-700/60 ring-1 ring-blue-500/30' : 'bg-blue-50/50 ring-1 ring-blue-300') : (d ? 'bg-gray-700/30' : 'bg-gray-50')}`}>
                  <div className="w-16 shrink-0">
                    <span className={`text-[12px] font-rasa font-bold ${
                      i >= 4 ? 'text-red-500' : i >= 3 ? 'text-orange-500' : (d ? 'text-gray-300' : 'text-gray-600')
                    }`}>{tier.label}</span>
                  </div>
                  <div className="flex-1">
                    <div className={`text-[12px] font-rasa ${d ? 'text-gray-300' : 'text-gray-600'}`}>{tier.subject}</div>
                    <div className={`text-[10px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{tier.tone}</div>
                  </div>
                  {i < 5 ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>After</span>
                      <input
                        type="number"
                        min="1"
                        max="60"
                        value={cadence[i] || (i + 1) * 5}
                        onChange={(e) => updateCadence(i, e.target.value)}
                        className={inputClass}
                      />
                      <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>days</span>
                    </div>
                  ) : (
                    <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>Auto</div>
                  )}
                  {i < 5 && (
                    <button
                      onClick={() => editingTier === tierNum ? setEditingTier(null) : openTierTemplate(tierNum)}
                      className={`text-[11px] font-rasa px-2 py-1 rounded shrink-0 ${
                        hasTemplate ? (d ? 'bg-blue-900/40 text-blue-300 hover:bg-blue-900/60' : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                        : (d ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
                      }`}
                    >
                      {hasTemplate ? 'Edit Template' : 'Add Template'}
                    </button>
                  )}
                </div>

              </div>
            );
          })}
        </div>

        <div className={`mt-3 p-3 rounded-lg ${d ? 'bg-blue-900/20 border border-blue-800/30' : 'bg-blue-50 border border-blue-100'}`}>
          <p className={`text-[11px] font-rasa ${d ? 'text-blue-300' : 'text-blue-700'}`}>
            <strong>How it works:</strong> After sending a file request, the first follow-up goes out after {cadence[0] || 5} business days. If still no response, Tier 2 goes at day {cadence[1] || 10}, and so on. Follow-ups auto-cancel when files are received. Tier 6 marks the property as unresponsive (no email sent).
          </p>
        </div>
      </div>

      {dirty && (
        <button onClick={save} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700">
          <Save size={14} /> Save Email Settings
        </button>
      )}

      {/* Template Editor Modal */}
      {editingTier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setEditingTier(null)}>
          <div className={`w-full max-w-lg rounded-xl shadow-2xl ${d ? 'bg-gray-800' : 'bg-white'}`} onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className={`flex items-center justify-between px-5 py-4 border-b ${d ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h3 className="text-sm font-dm-serif">Tier {editingTier} Email Template</h3>
                <p className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                  {TIER_INFO[editingTier - 1]?.tone} — {TIER_INFO[editingTier - 1]?.subject}
                </p>
              </div>
              <button onClick={() => setEditingTier(null)} className={`p-1.5 rounded-lg ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <span className={`text-lg ${d ? 'text-gray-400' : 'text-gray-500'}`}>×</span>
              </button>
            </div>

            {/* Modal body */}
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className={`block text-[12px] font-rasa font-bold mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Subject Line</label>
                <input
                  type="text"
                  value={templateForm.subject_template}
                  onChange={(e) => setTemplateForm({ ...templateForm, subject_template: e.target.value })}
                  placeholder={`${TIER_INFO[editingTier - 1]?.subject} — {{property_name}}`}
                  className={`w-full px-3 py-2 rounded-lg border text-sm font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500/50`}
                />
              </div>
              <div>
                <label className={`block text-[12px] font-rasa font-bold mb-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Email Body (HTML)</label>
                <textarea
                  value={templateForm.body_template}
                  onChange={(e) => setTemplateForm({ ...templateForm, body_template: e.target.value })}
                  placeholder="<p>Dear {{contact_name}},</p><p>This is a reminder regarding the outstanding audit files for <strong>{{property_name}}</strong>...</p>"
                  rows={8}
                  className={`w-full px-3 py-2 rounded-lg border text-sm font-rasa resize-none ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500/50`}
                />
              </div>
              <div>
                <label className={`block text-[11px] font-rasa font-bold mb-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Insert Variable</label>
                <div className="flex flex-wrap gap-1">
                  {['{{property_name}}','{{contact_name}}','{{audit_year}}','{{client_name}}','{{days_since_request}}','{{due_date}}','{{files_required}}','{{files_received}}','{{missing_files}}','{{auditor_name}}','{{auditor_email}}','{{tier_number}}'].map((v) => (
                    <button
                      key={v}
                      onClick={() => setTemplateForm({ ...templateForm, body_template: templateForm.body_template + v })}
                      className={`text-[11px] font-rasa px-1.5 py-0.5 rounded ${d ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <p className={`text-[10px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                Leave empty to use the default template for this tier. Custom templates override the default.
              </p>
            </div>

            {/* Modal footer */}
            <div className={`flex items-center justify-end gap-2 px-5 py-3 border-t ${d ? 'border-gray-700' : 'border-gray-200'}`}>
              <button onClick={() => setEditingTier(null)} className={`px-4 py-2 rounded-lg text-sm font-rasa ${d ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={saveTierTemplate} disabled={savingTemplate} className="px-4 py-2 rounded-lg text-sm font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {savingTemplate ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Dashboard Config Editor ──────────────────────────────────
const DashboardConfigEditor = ({ client, isDark: d }) => {
  const [config, setConfig] = useState({ hidden_columns: [], hidden_property_fields: [], custom_column_labels: {} });
  const [dirty, setDirty] = useState(false);
  const [newLabelKey, setNewLabelKey] = useState('');
  const [newLabelValue, setNewLabelValue] = useState('');

  useEffect(() => {
    const dc = client?.settings?.dashboard_config || {};
    setConfig({ hidden_columns: dc.hidden_columns || [], hidden_property_fields: dc.hidden_property_fields || [], custom_column_labels: dc.custom_column_labels || {} });
  }, [client]);

  const COLUMNS = ['building', 'unit', 'effective_date', 'certification_type', 'file_received_date', 'assigned_date', 'complete_date', 'provisional_approval_date', 'final_approval_date', 'audit_time', 'cycle_time_days', 'auditor', 'opened_count'];
  const PROP_FIELDS = ['assigned_to', 'start_date', 'management_company', 'comments'];

  const toggleCol = (col) => {
    setConfig((p) => ({ ...p, hidden_columns: p.hidden_columns.includes(col) ? p.hidden_columns.filter((c) => c !== col) : [...p.hidden_columns, col] }));
    setDirty(true);
  };

  const toggleField = (f) => {
    setConfig((p) => ({ ...p, hidden_property_fields: p.hidden_property_fields.includes(f) ? p.hidden_property_fields.filter((x) => x !== f) : [...p.hidden_property_fields, f] }));
    setDirty(true);
  };

  const addLabel = () => {
    if (!newLabelKey || !newLabelValue) return;
    setConfig((p) => ({ ...p, custom_column_labels: { ...p.custom_column_labels, [newLabelKey]: newLabelValue } }));
    setNewLabelKey(''); setNewLabelValue(''); setDirty(true);
  };

  const removeLabel = (key) => {
    setConfig((p) => { const l = { ...p.custom_column_labels }; delete l[key]; return { ...p, custom_column_labels: l }; });
    setDirty(true);
  };

  const save = async () => {
    try {
      const res = await apiPatch(`/admin/clients/${client.id}`, { settings: JSON.stringify({ ...client.settings, dashboard_config: config }) });
      if (res.ok) { toast.success('Dashboard config saved'); setDirty(false); } else toast.error('Failed');
    } catch (err) { toast.error(err.message); }
  };

  const chipClass = (active) => `text-[11px] font-rasa px-2 py-1 rounded cursor-pointer transition-colors ${
    active ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : (d ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
  }`;

  const inputClass = `px-3 py-1.5 rounded-lg border text-[12px] font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'} focus:outline-none focus:ring-1 focus:ring-blue-500/50`;

  return (
    <div className="space-y-5">
      {/* Hidden columns */}
      <div>
        <label className={`block text-[12px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Hidden Tenant File Columns</label>
        <p className={`text-[11px] font-rasa mb-2 ${d ? 'text-gray-500' : 'text-gray-400'}`}>Click to toggle — red = hidden from client view</p>
        <div className="flex flex-wrap gap-1.5">
          {COLUMNS.map((c) => <button key={c} onClick={() => toggleCol(c)} className={chipClass(config.hidden_columns.includes(c))}>{config.hidden_columns.includes(c) ? '✕ ' : ''}{c.replace(/_/g, ' ')}</button>)}
        </div>
      </div>

      {/* Hidden property fields */}
      <div>
        <label className={`block text-[12px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Hidden Property Fields</label>
        <div className="flex flex-wrap gap-1.5">
          {PROP_FIELDS.map((f) => <button key={f} onClick={() => toggleField(f)} className={chipClass(config.hidden_property_fields.includes(f))}>{config.hidden_property_fields.includes(f) ? '✕ ' : ''}{f.replace(/_/g, ' ')}</button>)}
        </div>
      </div>

      {/* Custom labels */}
      <div>
        <label className={`block text-[12px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Custom Column Labels</label>
        {Object.entries(config.custom_column_labels).length > 0 && (
          <div className="space-y-1 mb-2">
            {Object.entries(config.custom_column_labels).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-[12px] font-rasa">
                <span className={d ? 'text-gray-400' : 'text-gray-500'}>{k.replace(/_/g, ' ')}</span>
                <span className={d ? 'text-gray-600' : 'text-gray-300'}>→</span>
                <span className={d ? 'text-gray-200' : 'text-gray-700'}>{v}</span>
                <button onClick={() => removeLabel(k)} className="text-red-400 hover:text-red-500 text-[10px]">✕</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <select value={newLabelKey} onChange={(e) => setNewLabelKey(e.target.value)} className={inputClass}>
            <option value="">Column...</option>
            {COLUMNS.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
          <input type="text" value={newLabelValue} onChange={(e) => setNewLabelValue(e.target.value)} placeholder="Display as..." className={`w-36 ${inputClass}`} />
          <button onClick={addLabel} disabled={!newLabelKey || !newLabelValue} className="px-2 py-1.5 bg-blue-600 text-white rounded text-[11px] disabled:opacity-40">Add</button>
        </div>
      </div>

      {dirty && (
        <button onClick={save} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700">
          <Save size={14} /> Save Dashboard Config
        </button>
      )}
    </div>
  );
};


export default AuditSettingsPanel;
